import { useEffect, useState } from 'react';
import { campaignSchema, type CampaignFormInput } from '../../domain/campaignSchema';
import { listCampaignTypes, listCoordinators, type CoordinatorOption } from '../../api/campaigns';
import type { CampaignTypeRow } from '../../lib/db-types';

export interface CampaignFormValues {
  client: string;
  brand: string;
  /** Código de `campaign_types`. Cadena, no unión cerrada: la maestra manda. */
  type: string;
  description: string;
  name: string;
  analyticAccount: string;
  quota: string;
  startDate: string;
  endDate: string;
  coordinatorId: string;
  status: 'borrador' | 'activa' | 'cerrada';
}

export const EMPTY_CAMPAIGN_FORM: CampaignFormValues = {
  name: '', analyticAccount: '',
  client: '', brand: '', type: 'impulso', description: '', quota: '',
  startDate: '', endDate: '', coordinatorId: '', status: 'borrador',
};

type FieldErrors = Partial<Record<string, string>>;

export default function CampaniaDatosForm({
  initial, submitLabel, onSubmit, soloLectura = false, compactoPorDefecto = false,
}: {
  initial: CampaignFormValues;
  submitLabel: string;
  onSubmit: (parsed: CampaignFormInput) => Promise<void>;
  /**
   * Arranca mostrando SOLO lo mínimo para crear el asiento de la campaña, con el resto detrás de
   * "Más datos" (CA4). Pedido del usuario: *"simplemente poner las fechas de inicio, o sea, creas
   * el asiento de la campaña y después cuando lo abras, puedas subir la programación"*.
   *
   * Se hace escondiendo campos del MISMO formulario y no con un formulario aparte: dos caminos
   * para crear una campaña se desincronizan, y el que se usa menos es el que queda mal. Lo que
   * se esconde tiene un valor por defecto razonable (borrador, sin coordinador, sin cuota), así
   * que nada queda a medias por no haberlo visto.
   */
  compactoPorDefecto?: boolean;
  /** Ficha de consulta: el coordinador y el supervisor la LEEN, la edita el comercial dueño
   *  (RLS `campaigns_update` → `owns_campaign`). Se deshabilitan los campos además de
   *  esconder el botón: un campo editable que no guarda es peor que uno que no se toca. */
  soloLectura?: boolean;
}) {
  const [form, setForm] = useState<CampaignFormValues>(initial);
  const [coordinators, setCoordinators] = useState<CoordinatorOption[]>([]);
  const [tipos, setTipos] = useState<CampaignTypeRow[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [verTodo, setVerTodo] = useState(!compactoPorDefecto);

  useEffect(() => { setForm(initial); }, [initial]);

  // En modo consulta NO se pide la lista de coordinadores. La RLS de `profiles` solo deja al
  // coordinador y al supervisor verse a sí mismos y a la gente que tienen asignada, así que
  // el desplegable llegaría casi vacío y pintaría en blanco al coordinador real de la
  // campaña — un dato falso. Mejor no mostrar el campo (ver más abajo) que mostrarlo mal.
  useEffect(() => {
    if (soloLectura) return;
    listCoordinators()
      .then(setCoordinators)
      .catch(() => setGlobalError('No se pudo cargar la lista de coordinadores'));
  }, [soloLectura]);

  // Los tipos salen de la maestra `campaign_types`, incluso en modo consulta: si no, la ficha de
  // una campaña de mercaderismo mostraria el desplegable en blanco y pareceria que no tiene tipo.
  useEffect(() => {
    listCampaignTypes()
      .then(setTipos)
      .catch(() => setGlobalError('No se pudo cargar la lista de tipos de campaña'));
  }, []);

  function set<K extends keyof CampaignFormValues>(key: K, value: CampaignFormValues[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit() {
    setGlobalError(null);
    const quotaText = form.quota.trim();
    const quota = quotaText === '' ? null : Number(quotaText);
    if (quota !== null && !Number.isFinite(quota)) {
      setErrors({ quota: 'La cuota debe ser un número' });
      return;
    }
    const parsed = campaignSchema.safeParse({
      client: form.client,
      brand: form.brand,
      type: form.type,
      description: form.description,
      quota,
      startDate: form.startDate,
      endDate: form.endDate,
      coordinatorId: form.coordinatorId === '' ? null : form.coordinatorId,
      status: form.status,
    });
    if (!parsed.success) {
      const errs: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'global');
        if (!errs[key]) errs[key] = issue.message;
      }
      setErrors(errs);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await onSubmit(parsed.data);
    } catch (e) {
      setGlobalError(e instanceof Error ? e.message : 'No se pudo guardar la campaña');
    } finally {
      setSaving(false);
    }
  }

  const err = (k: string) => (errors[k] ? <span className="text-sm text-red-600">{errors[k]}</span> : null);

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* El nombre va primero porque es como la operación llama a la campaña. Y la cuenta
          analítica es EDITABLE porque llega tarde: la hoja de agosto trae tres campañas sin
          ella, y bloquear la carga por eso dejaría fuera campañas que ya están corriendo. */}
      <label className="text-sm col-span-2">Nombre de la campaña (opcional)
        <input disabled={soloLectura} className="border rounded px-2 py-1 w-full disabled:bg-gray-100"
          placeholder="Ej. Troperos Pringles" value={form.name}
          onChange={(e) => set('name', e.target.value)} />
      </label>
      {verTodo && (
      <label className="text-sm">Cuenta analítica (opcional)
        <input disabled={soloLectura} className="border rounded px-2 py-1 w-full disabled:bg-gray-100"
          placeholder="Ej. S000169934" value={form.analyticAccount}
          onChange={(e) => set('analyticAccount', e.target.value)} />
      </label>
      )}
      <label className="text-sm">Cliente
        <input disabled={soloLectura} className="border rounded px-2 py-1 w-full disabled:bg-gray-100" value={form.client}
          onChange={(e) => set('client', e.target.value)} />
        {err('client')}
      </label>
      <label className="text-sm">Marca
        <input disabled={soloLectura} className="border rounded px-2 py-1 w-full disabled:bg-gray-100" value={form.brand}
          onChange={(e) => set('brand', e.target.value)} />
        {err('brand')}
      </label>
      <label className="text-sm">Tipo
        <select disabled={soloLectura} className="border rounded px-2 py-1 w-full disabled:bg-gray-100" value={form.type}
          onChange={(e) => set('type', e.target.value as CampaignFormValues['type'])}>
          {/* El tipo actual va siempre, aunque la maestra todavía no haya llegado o el tipo
              se haya desactivado: sin esto el desplegable se pinta vacío y guardar cambiaría
              el tipo de la campaña sin que nadie lo pidiera. */}
          {tipos.length === 0 && <option value={form.type}>{form.type}</option>}
          {tipos.map((t) => (
            <option key={t.code} value={t.code}>{t.name}</option>
          ))}
          {tipos.length > 0 && !tipos.some((t) => t.code === form.type) && (
            <option value={form.type}>{form.type} (tipo retirado)</option>
          )}
        </select>
      </label>
      {verTodo && (
      <label className="text-sm">Cuota (opcional)
        <input disabled={soloLectura} className="border rounded px-2 py-1 w-full disabled:bg-gray-100" inputMode="decimal" value={form.quota}
          onChange={(e) => set('quota', e.target.value)} />
        {err('quota')}
      </label>
      )}
      {verTodo && (
      <label className="text-sm col-span-2">Descripción / dinámica
        <textarea disabled={soloLectura} className="border rounded px-2 py-1 w-full disabled:bg-gray-100" rows={3} value={form.description}
          onChange={(e) => set('description', e.target.value)} />
      </label>
      )}
      <label className="text-sm">Fecha inicio
        <input type="date" disabled={soloLectura} className="border rounded px-2 py-1 w-full disabled:bg-gray-100" value={form.startDate}
          onChange={(e) => set('startDate', e.target.value)} />
        {err('startDate')}
      </label>
      <label className="text-sm">Fecha fin
        <input type="date" disabled={soloLectura} className="border rounded px-2 py-1 w-full disabled:bg-gray-100" value={form.endDate}
          onChange={(e) => set('endDate', e.target.value)} />
        {err('endDate')}
      </label>
      {!soloLectura && verTodo && (
        <label className="text-sm">Coordinador
          <select className="border rounded px-2 py-1 w-full" value={form.coordinatorId}
            onChange={(e) => set('coordinatorId', e.target.value)}>
            <option value="">— Sin coordinador —</option>
            {coordinators.map((c) => (
              <option key={c.id} value={c.id}>{c.first_names} {c.last_names}</option>
            ))}
          </select>
          {err('coordinatorId')}
        </label>
      )}
      {verTodo && (
      <label className="text-sm">Estado
        <select disabled={soloLectura} className="border rounded px-2 py-1 w-full disabled:bg-gray-100" value={form.status}
          onChange={(e) => set('status', e.target.value as CampaignFormValues['status'])}>
          <option value="borrador">Borrador</option>
          <option value="activa">Activa</option>
          <option value="cerrada">Cerrada</option>
        </select>
      </label>
      )}
      {!verTodo && !soloLectura && (
        <button
          type="button"
          onClick={() => setVerTodo(true)}
          className="col-span-2 justify-self-start text-sm text-blue-700 underline"
        >
          Más datos (cuenta analítica, cuota, descripción, coordinador, estado)
        </button>
      )}
      {globalError && <p className="col-span-2 text-red-600">{globalError}</p>}
      {!soloLectura && (
        <div className="col-span-2 flex justify-end">
          <button className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
            disabled={saving} onClick={() => void submit()}>
            {saving ? 'Guardando…' : submitLabel}
          </button>
        </div>
      )}
    </div>
  );
}
